'use client';

import {useCallback} from 'react';
import Link from 'next/link';
import {useSession} from 'next-auth/react';
import {motion} from 'framer-motion';
import type {Recommendation} from '@manaboo/recommendation';
import type {Campaigns, CampaignsGames, CampaignsUsers, Games} from '@manaboo/db/types';
import {cn} from '@manaboo/ui/cn';
import {useAnimatedGradient} from '@manaboo/ui/hooks/use-animated-gradient';
import {Progress} from '@manaboo/ui/progress';
import {Badge} from '@manaboo/ui/badge';
import {formatDays} from '@manaboo/utils/text';
import {formatPoints} from '@manaboo/utils/points';
import {CoinsBoos} from '@manaboo/ui/icons';
import {Button} from '@manaboo/ui/button';
import {useAuthDialog} from '@/hooks/use-dialog-auth';
import {insertRecommendationClick} from '@/actions/insert-recommendation-click';
import {Play} from 'lucide-react';

interface PropsCardGame {
	game: Pick<
		Games,
		| 'id'
		| 'name'
		| 'provider_id'
		| 'provider_game_id'
		| 'provider_group_id'
		| 'thumbnail_small'
		| 'video_hash'
		| 'genre_1'
		| 'genre_2'
		| 'genre_3'
	> & {
		campaign?: {
			game_id: CampaignsGames['game_id'];
			campaign_id: CampaignsGames['campaign_id'];
			rate: Campaigns['rate'];
			status: CampaignsUsers['status'];
			conditions_days_completed: CampaignsUsers['conditions_days_completed'];
			condition_days: Campaigns['condition_days'];
		};
	};
	recommendation?: {
		version_id: Recommendation['version_id'];
		section_id: Recommendation['id'];
		position: number;
	};
	index?: number;
}

export function CardGame({game, recommendation, index}: PropsCardGame) {
	const {data: session} = useSession();
	const {openDialog} = useAuthDialog();
	const {animatedBorder} = useAnimatedGradient(Boolean(game.campaign));

	const handleClick = useCallback(
		async (e: React.MouseEvent<HTMLAnchorElement>) => {
			if (!session?.user) {
				e.preventDefault();
				openDialog();
				return;
			}

			if (recommendation) {
				try {
					await insertRecommendationClick({
						game_id: game.id,
						campaign_id: game?.campaign?.campaign_id ?? null,
						recommendation_version_id: recommendation.version_id,
						recommendation_section_id: recommendation.section_id,
						position: recommendation.position,
					});
				} catch (error) {
					console.error('Failed to track recommendation click:', error);
				}
			}
		},
		[session?.user, openDialog, recommendation],
	);

	return (
		<div
			className={cn('rounded-md', game.campaign ? 'p-[3px] md:mb-2 mb-1' : '')}
			style={animatedBorder}
		>
			<Link
				className="group flex h-full flex-col overflow-hidden rounded-md"
				href={session?.user ? `/gry/${game.id}` : '#'}
				onClick={handleClick}
				data-testid="card-game-link"
			>
				<div
					className={cn(
						'relative h-[150px] w-full overflow-hidden',
						game?.campaign ? 'rounded-none' : 'rounded-md',
					)}
				>
					{game.video_hash ? (
						<div className="relative h-full w-full transition-transform duration-300 hover:scale-105">
							<iframe
								title={game.name ?? ''}
								src={`https://www.youtube-nocookie.com/embed/${game.video_hash}?autoplay=1&mute=1&controls=0&disablekb=1&fs=0&loop=1&playsinline=1&playlist=${game.video_hash}`}
								frameBorder="0"
								className="pointer-events-none absolute left-1/2 top-1/2 h-[calc(100%_+_120px)] max-h-[150px] min-h-[calc(100%_+_120px)] w-[calc(100%_+_120px)] min-w-[calc(100%_+_120px)] -translate-x-1/2 -translate-y-1/2 brightness-75"
							/>
						</div>
					) : (
						<img
							alt={game.name ?? ''}
							className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
							height={150}
							loading="lazy"
							src={
								game.thumbnail_small
									? game.thumbnail_small
									: '/img/placeholder-thumbnail-small.jpg'
							}
							width={382}
						/>
					)}

					{game.campaign ? (
						<Badge className="pointer-events-none absolute left-1/2 top-2 w-max max-w-full -translate-x-1/2 text-center font-bold text-black">
							<CoinsBoos className="mr-1 h-4 w-4" />
							{game.campaign.conditions_days_completed ===
							game.campaign.condition_days
								? 'Misja ukończona!'
								: `Nagroda: ${formatPoints(game.campaign.rate)}`}
						</Badge>
					) : null}

					{index !== undefined ? (
						<Button
							size="icon"
							className="pointer-events-none absolute left-1 top-1 text-xl"
						>
							{index + 1}
						</Button>
					) : null}
				</div>

				<div
					className={cn(
						'bg-background flex flex-grow flex-col py-2',
						game.campaign ? 'px-2 py-3' : '',
					)}
				>
					<div className="flex-grow">
						<h3 className="line-clamp-1 text-base">{game.name}</h3>

						{/*<div className="text-muted-foreground text-xs">*/}
						{/*	{`${game.genre_1 ? game.genre_1 : ''} ${*/}
						{/*		game.genre_2 ? `- ${game.genre_2}` : ''*/}
						{/*	} ${game.genre_3 ? `- ${game.genre_3}` : ''}`}*/}
						{/*</div>*/}
					</div>

					{game?.campaign?.conditions_days_completed ? (
						<div className="mt-2 flex flex-col gap-1">
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-1 text-xs">
									Ukończyłeś:
								</div>
								<span className="text-muted-foreground flex-shrink-0 text-xs">
									{game.campaign.conditions_days_completed} /{' '}
									{formatDays(game.campaign.condition_days)}
								</span>
							</div>

							<Progress
								className="h-2"
								value={
									(game.campaign.conditions_days_completed /
										game.campaign.condition_days) *
									100
								}
							/>
						</div>
					) : null}

					<motion.div
						className={cn(
							'mt-2 transition-opacity duration-200',
							!game.campaign && 'opacity-0 group-hover:opacity-100',
						)}
						initial={{y: 10}}
						animate={{y: 0}}
						transition={{type: 'spring', stiffness: 300, damping: 20}}
					>
						<Button
							size="sm"
							className="w-full"
						>
							<Play className="mr-1 h-4 w-4" />
							Graj teraz
						</Button>
					</motion.div>
				</div>
			</Link>
		</div>
	);
}
